import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import {
  IUser,
  IUserWithStats,
  IUserDetail,
  IPaginated,
  IDashboardStats,
} from '../../types';

// --- Interfaces ---

export interface IUserListParams {
  page: number;
  search: string;
  filter: string;
  limit: number;
}

export interface ICreateUserData {
  username: string;
  displayName: string;
  password: string;
  mobileNumber?: string;
  countryCode?: string;
}

export interface IUpdateUserData {
  displayName: string;
  mobileNumber?: string;
  countryCode?: string;
  password?: string;
}

export interface IBanUserData {
  userId: string;
  reason: string;
}

export interface IMuteUserData {
  userId: string;
  reason: string;
  mutedUntil?: string;
}

// --- Query Key Factory ---

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: IUserListParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

// --- Helpers ---

const buildQueryString = (params: IUserListParams): string => {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.append('search', params.search);
  searchParams.append('page', String(params.page));
  searchParams.append('limit', String(params.limit));

  if (params.filter && params.filter !== 'all') {
    searchParams.append('filter', params.filter);
  }

  return searchParams.toString();
};

// --- Query Hooks ---

export const useDashboardStats = () =>
  useQuery({
    queryKey: ['dashboard-stats'] as const,
    queryFn: () => api.get<IDashboardStats>('/backoffice/dashboard-stats'),
  });

export const useUsers = (params: IUserListParams) =>
  useQuery({
    queryKey: userKeys.list(params),
    queryFn: () =>
      api.get<IPaginated<IUserWithStats>>(
        `/backoffice/users?${buildQueryString(params)}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useUserDetail = (id: string) =>
  useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => api.get<IUserDetail>(`/backoffice/users/${id}/detail`),
    enabled: !!id,
  });

// --- Mutation Hooks ---

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ICreateUserData) =>
      api.post<IUser>('/admin/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      // Delay stats refetch to allow the 2s subscriber buffer to flush
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      }, 2500);
      toast.success(t('toast.user_created'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_create_failed'));
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateUserData }) =>
      api.patch<IUser>(`/admin/users/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.id),
      });
      toast.success(t('toast.user_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_update_failed'));
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/admin/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      }, 2500);
      toast.success(t('toast.user_deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_delete_failed'));
    },
  });
};

export const useBanUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId, reason }: IBanUserData) =>
      api.post<void>(`/admin/users/${userId}/ban`, { reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      }, 2500);
      toast.success(t('toast.user_banned'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_ban_failed'));
    },
  });
};

export const useUnbanUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      api.post<void>(`/admin/users/${userId}/unban`),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(userId),
      });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      }, 2500);
      toast.success(t('toast.user_unbanned'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_unban_failed'));
    },
  });
};

export const useMuteUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId, reason, mutedUntil }: IMuteUserData) => {
      const payload: Record<string, unknown> = { reason };

      if (mutedUntil) {
        payload.mutedUntil = mutedUntil;
      }

      return api.post<void>(`/admin/users/${userId}/mute`, payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
      toast.success(t('toast.user_muted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_mute_failed'));
    },
  });
};

export const useUnmuteUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      api.post<void>(`/admin/users/${userId}/unmute`),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(userId),
      });
      toast.success(t('toast.user_unmuted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.user_unmute_failed'));
    },
  });
};
